import { apiClient } from './api/client'
import type {
  AssignableDeveloper,
  SystemIssue,
  SystemIssueMentionCandidate,
  SystemIssueMessage,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../features/system-issues/types/system-issues'
import type { UserRef } from '../types/user'

export interface SystemIssuePendingAttachment {
  gcsPath: string
  name: string
  size: number
  type: string
}

export const systemIssuesService = {
  // ============================ Issues ============================
  /** Reportes do próprio usuário — disponível pra qualquer usuário interno. */
  async listMine(): Promise<SystemIssue[]> {
    const { data } = await apiClient.get<SystemIssue[]>('/system-issues/mine')
    return data
  },

  /** Atendimento HUB (não-dev): reportes da mesma empresa + os meus. */
  async listCompany(): Promise<SystemIssue[]> {
    const { data } = await apiClient.get<SystemIssue[]>(
      '/system-issues/company',
    )
    return data
  },

  /** Fila de triagem com TODOS os reportes — dev-only (backend enforça). */
  async listAll(
    opts: {
      status?: SystemIssueStatus
      assignedToId?: number
      companyId?: number
    } = {},
  ): Promise<SystemIssue[]> {
    const { data } = await apiClient.get<SystemIssue[]>('/system-issues', {
      params: opts,
    })
    return data
  },

  /**
   * Problemas do Hub vinculados a UM processo — aba "HUB" dentro de
   * Ocorrências. Visibilidade é do processo (qualquer interno do CC dele),
   * diferente de `listMine` (reporter/marcado).
   */
  async listByProcess(processId: number): Promise<SystemIssue[]> {
    const { data } = await apiClient.get<SystemIssue[]>(
      `/system-issues/by-process/${processId}`,
    )
    return data
  },

  async getIssue(issueId: number): Promise<SystemIssue> {
    const { data } = await apiClient.get<SystemIssue>(
      `/system-issues/${issueId}`,
    )
    return data
  },

  async createIssue(payload: {
    title: string
    description: string
    priority?: SystemIssuePriority
    related_process_id?: number
    attachments?: SystemIssuePendingAttachment[]
    /** Ausente = empresa principal (backend). */
    company_id?: number
  }): Promise<{ issue: SystemIssue; firstMessage: SystemIssueMessage }> {
    const { data } = await apiClient.post('/system-issues', payload)
    return data
  },

  // ============================ Menções ============================
  /** Candidatos pro formulário de criação — colegas do PRÓPRIO CC do usuário. */
  async listMentionCandidates(
    companyId?: number | null,
  ): Promise<SystemIssueMentionCandidate[]> {
    const { data } = await apiClient.get<SystemIssueMentionCandidate[]>(
      '/system-issues/mention-candidates',
      { params: { company_id: companyId ?? undefined } },
    )
    return data
  },

  /** Candidatos pra marcar numa issue já criada — colegas do CC do REPORTER. */
  async listMentionCandidatesForIssue(
    issueId: number,
  ): Promise<SystemIssueMentionCandidate[]> {
    const { data } = await apiClient.get<SystemIssueMentionCandidate[]>(
      `/system-issues/${issueId}/mention-candidates`,
    )
    return data
  },

  async tagMentions(issueId: number, accountIds: number[]): Promise<UserRef[]> {
    const { data } = await apiClient.post<UserRef[]>(
      `/system-issues/${issueId}/mentions`,
      { accountIds },
    )
    return data
  },

  async untagMention(issueId: number, accountId: number): Promise<void> {
    await apiClient.delete(`/system-issues/${issueId}/mentions/${accountId}`)
  },

  /** Dev-only (backend enforça). */
  async updateStatus(
    issueId: number,
    status: SystemIssueStatus,
  ): Promise<SystemIssue> {
    const { data } = await apiClient.patch<SystemIssue>(
      `/system-issues/${issueId}/status`,
      { status },
    )
    return data
  },

  /** Dev-only (backend enforça). */
  async updatePriority(
    issueId: number,
    priority: SystemIssuePriority,
  ): Promise<SystemIssue> {
    const { data } = await apiClient.patch<SystemIssue>(
      `/system-issues/${issueId}/priority`,
      { priority },
    )
    return data
  },

  /** Dev-only (backend enforça). Aceita se atribuir ou atribuir a outro dev. */
  async assign(issueId: number, assignedToId: number): Promise<SystemIssue> {
    const { data } = await apiClient.patch<SystemIssue>(
      `/system-issues/${issueId}/assign`,
      { assignedToId },
    )
    return data
  },

  /** Dev-only (backend enforça). Lista pro dropdown "Atribuir a". */
  async listAssignableDevelopers(): Promise<AssignableDeveloper[]> {
    const { data } = await apiClient.get<AssignableDeveloper[]>(
      '/system-issues/developers',
    )
    return data
  },

  /** Marca a issue como lida por mim até agora — zera meu unread_count nela. */
  async markRead(issueId: number): Promise<void> {
    await apiClient.post(`/system-issues/${issueId}/read`)
  },

  // ============================ Messages ============================
  async listMessages(issueId: number): Promise<SystemIssueMessage[]> {
    const { data } = await apiClient.get<SystemIssueMessage[]>(
      `/system-issues/${issueId}/messages`,
    )
    return data
  },

  async createMessage(
    issueId: number,
    payload: { content: string; attachments?: SystemIssuePendingAttachment[] },
  ): Promise<SystemIssueMessage> {
    const { data } = await apiClient.post(
      `/system-issues/${issueId}/messages`,
      payload,
    )
    return data
  },

  // ============================ Attachments ============================
  /**
   * Etapa 1 do upload — sem `issueId` (o anexo pode ser enviado antes da
   * issue existir, no formulário de "novo problema"). Mesma forma de retorno
   * de `occurrencesService.prepareAttachmentUploads`.
   */
  async prepareAttachmentUploads(
    files: Array<{ originalName: string; contentType: string; size: number }>,
  ): Promise<
    Array<{
      uploadUrl: string
      gcsPath: string
      name: string
      type: string
      size: number
      uploadHeaders?: Record<string, string>
    }>
  > {
    const { data } = await apiClient.post('/system-issues/uploads/prepare', {
      files,
    })
    return data
  },

  async getAttachmentDownloadUrl(
    messageId: number,
    index: number,
  ): Promise<string> {
    const { data } = await apiClient.get<{ url: string }>(
      `/system-issues/messages/${messageId}/attachments/${index}/url`,
    )
    return data.url
  },
}
